import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { consulentiApi, authApi } from '../api';
import Modal from '../components/Modal';
import AssegnaCommesse from '../components/AssegnaCommesse';
import { useAuth } from '../context/AuthContext';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const empty = { tipo: 'persona', nome: '', cognome: '', referente: '', consulente_riferimento_id: '', indirizzo: '', pec: '', email: '', telefono: '', partita_iva: '', specializzazione: '', tariffa_giornaliera: '', note: '', tar_front_ora: '', tar_front_giorno: '', tar_front_mezza: '', tar_back_ora: '', tar_back_giorno: '', tar_back_mezza: '', tariffa_km: '' };

export default function Consulenti() {
  // Creare un account è cosa da amministratore: a chi non può, l'offerta di
  // farlo da qui sarebbe solo un errore annunciato.
  const { user } = useAuth();
  const puoCreareAccessi = user?.ruolo === 'admin';
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => consulentiApi.list({ search }).then(setRows);
  useEffect(() => { load(); }, [search]);

  const openNew = () => { setForm(empty); setModal({ mode: 'new' }); };
  const openEdit = (r) => { setForm(r); setModal({ mode: 'edit', id: r.id }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'new') await consulentiApi.create(form);
      else await consulentiApi.update(modal.id, form);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('Eliminare questo consulente?')) return;
    await consulentiApi.delete(id); load();
  };

  // Assegnazione commesse dal pannello consulenti. L'assegnazione vive
  // sull'ACCOUNT dell'utente (è quello che entra nel portale), quindi serve
  // trovare l'account collegato al profilo consulente.
  const [assegna, setAssegna] = useState(null);   // { consulente, user | null }
  const apriAssegnazione = async (r) => {
    try {
      const utenti = await authApi.usersList();
      const user = utenti.find(u => u.consulente_id === r.id && u.attivo);
      setAssegna({ consulente: r, user: user || null });
      setCreaAccesso(null); setErroreAccesso('');
    } catch (e) { alert(e); }
  };

  // Accesso al portale creato dal profilo consulente: la stessa persona non va
  // ribattuta in Utenti e Accessi, e il collegamento fra le due schede — che
  // è quello che poi fa funzionare la rendicontazione — nasce già fatto.
  const [creaAccesso, setCreaAccesso] = useState(null);
  const [creandoAccesso, setCreandoAccesso] = useState(false);
  const [erroreAccesso, setErroreAccesso] = useState('');

  const senzaAccenti = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
  const apriCreazioneAccesso = (c) => {
    const nome = senzaAccenti(c.nome).trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const cognome = senzaAccenti(c.cognome).trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    setErroreAccesso('');
    setCreaAccesso({
      username: [nome.slice(0, 1), cognome].filter(Boolean).join('') || nome,
      // Provvisoria e leggibile al telefono: la cambierà lui al primo accesso.
      password: `Sinthema${Math.floor(1000 + Math.random() * 9000)}`,
      email: c.email || '',
      iniziali: `${(c.nome || '').trim().slice(0, 1)}${(c.cognome || '').trim().slice(0, 1)}`.toUpperCase(),
    });
  };

  const creaUtenteConsulente = async () => {
    setCreandoAccesso(true); setErroreAccesso('');
    try {
      const c = assegna.consulente;
      await authApi.createUser({
        username: creaAccesso.username.trim(),
        password: creaAccesso.password,
        nome: c.nome, cognome: c.cognome || '',
        iniziali: creaAccesso.iniziali.trim(),
        email: creaAccesso.email || null,
        ruolo: 'esterno', consulente_id: c.id,
      });
      // Si rientra dalla porta principale: l'elenco utenti ora lo contiene, e
      // la finestra passa da sola all'assegnazione delle commesse.
      await apriAssegnazione(c);
    } catch (e) {
      setErroreAccesso(String(e?.error || e));
    } finally { setCreandoAccesso(false); }
  };

  const f = (k) => ({ value: form[k] || '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Consulenti e società</h1>
        <div className="flex gap-2">
          <button className="btn-secondary"
                  onClick={() => { setForm({ ...empty, tipo: 'societa' }); setModal({ mode: 'new' }); }}>
            + Società
          </button>
          <button className="btn-primary" onClick={openNew}>+ Nuovo consulente</button>
        </div>
      </div>
      <input className="input max-w-xs" placeholder="Cerca per nome..." value={search} onChange={e => setSearch(e.target.value)} />

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Specializzazione</th>
              <th className="px-4 py-3">Tariffa gg</th>
              <th className="px-4 py-3">P.IVA</th>
              <th className="px-4 py-3">Ore tot.</th>
              <th className="px-4 py-3">Costo tot.</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Nessun consulente</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">
                  {r.nome} {r.cognome}
                  {r.tipo === 'societa' && (
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 align-middle">
                      società
                    </span>
                  )}
                  {r.referente && <span className="block text-xs font-normal text-gray-500">{r.referente}</span>}
                </td>
                <td className="px-4 py-3 text-gray-600">{r.email || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{r.specializzazione || '—'}</td>
                <td className="px-4 py-3">{r.tariffa_giornaliera ? fmt(r.tariffa_giornaliera) : '—'}</td>
                <td className="px-4 py-3 text-gray-500">{r.partita_iva || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{r.totale_ore}h</td>
                <td className="px-4 py-3 font-medium text-red-600">{fmt(r.totale_costi)}</td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <button className="btn-sm btn bg-brand-50 text-brand-700 hover:bg-brand-100 whitespace-nowrap"
                          title="Assegna le commesse su cui può rendicontare"
                          onClick={() => apriAssegnazione(r)}>Commesse</button>
                  <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}>Modifica</button>
                  <button className="btn-danger btn-sm" onClick={() => del(r.id)}>Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={(form.tipo === 'societa' ? 'Società' : 'Consulente') + (modal.mode === 'new' ? ' — nuovo' : ' — modifica')} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-4">
            {/* Non tutto il lavoro comprato viene da una persona: a una società
                si emette un ordine di acquisto, e l'anagrafica cambia di
                conseguenza — ragione sociale e referente al posto di nome e
                cognome. Il resto (tariffe, costi, controlli) è lo stesso. */}
            <div className="col-span-2">
              <label className="label">Tipo</label>
              <div className="flex gap-2">
                {[['persona', 'Persona'], ['societa', 'Società']].map(([v, l]) => (
                  <button key={v} type="button"
                          className={`btn-sm btn ${(form.tipo || 'persona') === v
                            ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                          onClick={() => setForm(p => ({ ...p, tipo: v }))}>{l}</button>
                ))}
              </div>
            </div>

            {form.tipo === 'societa' ? (
              <>
                <div className="col-span-2">
                  <label className="label">Ragione sociale *</label>
                  <input className="input" {...f('nome')} />
                </div>
                <div><label className="label">Referente</label><input className="input" {...f('referente')} /></div>
                <div>
                  <label className="label">Consulente di riferimento</label>
                  <select className="input" value={form.consulente_riferimento_id || ''}
                          onChange={e => setForm(p => ({ ...p, consulente_riferimento_id: e.target.value }))}>
                    <option value="">— nessuno —</option>
                    {rows.filter(r => r.tipo !== 'societa' && r.id !== modal.id)
                         .map(r => <option key={r.id} value={r.id}>{r.nome} {r.cognome}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Facoltativo: chi lavora attraverso questa società, se è qualcuno che conosciamo.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div><label className="label">Nome *</label><input className="input" {...f('nome')} /></div>
                <div><label className="label">Cognome *</label><input className="input" {...f('cognome')} /></div>
              </>
            )}
            <div><label className="label">Email</label><input className="input" type="email" {...f('email')} /></div>
            <div><label className="label">Telefono</label><input className="input" {...f('telefono')} /></div>
            <div><label className="label">P.IVA</label><input className="input" {...f('partita_iva')} /></div>
            <div><label className="label">Specializzazione</label><input className="input" {...f('specializzazione')} /></div>
            {/* Indirizzo e PEC servono a intestare l'ordine di acquisto */}
            <div className="col-span-2"><label className="label">Indirizzo</label><input className="input" {...f('indirizzo')} /></div>
            <div className="col-span-2"><label className="label">PEC</label><input className="input" {...f('pec')} /></div>
            <div><label className="label">Tariffa giornaliera (€)</label><CampoImporto className="input" value={form.tariffa_giornaliera} onChange={v => setForm(p => ({ ...p, tariffa_giornaliera: v }))} /></div>

            <div className="col-span-2 bg-gray-50 border rounded-lg p-3 space-y-2">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Tariffe concordate con il consulente
              </div>
              <p className="text-xs text-gray-500">
                Quanto riconosciamo a questa persona sulle commesse in economia.
                Valgono per tutte le commesse, salvo deroga sulla singola.
              </p>
              <div className="grid grid-cols-4 gap-2 items-end">
                <div></div>
                <div className="text-xs font-medium text-gray-600">Giornata intera</div>
                <div className="text-xs font-medium text-gray-600">Mezza giornata</div>
                <div className="text-xs font-medium text-gray-600">Oraria</div>

                <div className="text-sm text-gray-700">Front office</div>
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_front_giorno')} />
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_front_mezza')} />
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_front_ora')} />

                <div className="text-sm text-gray-700">Back office</div>
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_back_giorno')} />
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_back_mezza')} />
                <input className="input py-1 text-sm" type="number" step="0.01" {...f('tar_back_ora')} />
              </div>

              <div className="grid grid-cols-4 gap-2 items-center pt-2 border-t">
                <div className="text-sm text-gray-700">Rimborso km</div>
                <input className="input py-1 text-sm" type="number" step="0.01" placeholder="0,45"
                       {...f('tariffa_km')} />
                <div className="col-span-2 text-xs text-gray-500">
                  €/km riconosciuti. Vuoto: vale la tariffa della commessa, altrimenti 0,45 €/km.
                </div>
              </div>
            </div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={3} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}

      {assegna && (
        <Modal title={`Commesse di ${assegna.consulente.nome} ${assegna.consulente.cognome || ''}`}
               onClose={() => setAssegna(null)} size="lg">
          {assegna.user ? (
            <AssegnaCommesse user={assegna.user} />
          ) : (
            // L'accesso al portale non serve per lavorare con un consulente:
            // incarichi, offerte e costi funzionano lo stesso. Serve solo se
            // deve rendicontare da sé. E quando serve, si crea da qui senza
            // ribattere nome, cognome e mail in un'altra pagina.
            <div className="text-sm text-gray-600 space-y-3 py-2">
              <p>
                Questo consulente non ha un accesso al portale, e per molti incarichi non
                serve: l'incarico, le sue offerte e i costi funzionano comunque. L'accesso
                serve se deve <strong>rendicontare da sé</strong> i SAL — e le commesse si
                assegnano a quell'account.
              </p>
              {!puoCreareAccessi ? (
                <p className="text-xs text-gray-500">
                  L'accesso lo crea un amministratore, da <strong>Utenti e Accessi</strong>.
                </p>
              ) : !creaAccesso ? (
                <button className="btn-primary" onClick={() => apriCreazioneAccesso(assegna.consulente)}>
                  Crea l'accesso al portale
                </button>
              ) : (
                <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-500">
                    Nome, cognome e mail sono quelli del profilo consulente: l'account nasce
                    già collegato a lui.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Nome utente</label>
                      <input className="input" value={creaAccesso.username}
                             onChange={e => setCreaAccesso(p => ({ ...p, username: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Password</label>
                      <input className="input font-mono" value={creaAccesso.password}
                             onChange={e => setCreaAccesso(p => ({ ...p, password: e.target.value }))} />
                      <p className="text-xs text-gray-400 mt-0.5">Da comunicare al consulente.</p>
                    </div>
                    <div>
                      <label className="label">Email</label>
                      <input className="input" value={creaAccesso.email}
                             onChange={e => setCreaAccesso(p => ({ ...p, email: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Iniziali</label>
                      <input className="input uppercase" value={creaAccesso.iniziali}
                             onChange={e => setCreaAccesso(p => ({ ...p, iniziali: e.target.value.toUpperCase() }))} />
                    </div>
                  </div>
                  {erroreAccesso && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                      {erroreAccesso}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button className="btn-primary" disabled={creandoAccesso} onClick={creaUtenteConsulente}>
                      {creandoAccesso ? 'Creo…' : 'Crea l’accesso'}
                    </button>
                    <button className="btn-secondary" onClick={() => setCreaAccesso(null)}>Annulla</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <button className="btn-secondary" onClick={() => setAssegna(null)}>Chiudi</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
